<template>
    <div v-if="visible" class="agent-mediation">
        {{ label }}
        <ButtonLink v-if="multiple" size="m" :onClick="toggleModal">
            {{ t({ key: 'shared.read_more' }) }}
        </ButtonLink>
    </div>
</template>

<script lang="ts">
    import store from '@/store';
    import { defineComponent, computed } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { ModalType } from '@/store/general/types';
    import { Market } from '@shared/config/market/market.models';
    import { isStenaCompany } from '@shared/config/market/market.helpers';
    import { ProductType } from '@/api/Cart/cart.interfaces';
    import { isNotNil } from '@shared/utils/value';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'AgentMediation',
        components: { ButtonLink },
        methods: { t },
        setup() {
            const market = computed((): Market => store.getters['general/getMarket']);

            const isStena = computed(() =>
                market.value ? isStenaCompany(market.value.number) : false
            );

            const visible = computed<boolean>(() => !isStena.value && types.value.length > 0);

            const hasActivity = computed(() => store.getters['cart/hasActivityProducts']);

            const hasFlight = computed(() => store.getters['cart/hasFlightProducts']);

            const hasFerry = computed(() => store.getters['cart/hasFerryProducts']);

            const types = computed<ProductType[]>(() => {
                return [
                    hasActivity.value ? ProductType.Activity : null,
                    hasFlight.value ? ProductType.Flight : null,
                    hasFerry.value ? ProductType.Ferry : null,
                ].filter(isNotNil);
            });

            const count = computed<number>(() => types.value.length);
            const multiple = computed<boolean>(() => count.value > 1);

            const label = computed<string | null>(() => {
                if (multiple.value) {
                    return t({
                        key: 'checkout.agent_mediation_label',
                        values: [market.value.name],
                    });
                }

                if (count.value === 1) {
                    const [type] = types.value;

                    switch (type) {
                        case ProductType.Activity:
                            return t({
                                key: 'checkout.agent_mediation_ticket',
                                values: [market.value.name],
                            });
                        case ProductType.Flight:
                            return t({
                                key: 'checkout.agent_mediation_flight',
                                values: [market.value.name],
                            });
                        case ProductType.Ferry:
                            return t({
                                key: 'checkout.agent_mediation_ferry',
                                values: [market.value.name],
                            });
                    }
                }

                return null;
            });

            const toggleModal = () => {
                store.dispatch('general/setModal', {
                    type: ModalType.AgentMediation,
                });
            };

            return {
                label,
                multiple,
                visible,
                toggleModal,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'AgentMediation';
</style>
